import { apiRequest } from '../../../shared/services/apiClient';
import type { CreateScheduleRuleInput, DayOfWeek, UpdateScheduleRuleInput } from '../../calendar/types/Calendar';
import type { ScheduleRepeatPattern } from '../../companies/types/Company';
import type { Employee } from '../../admin/types/Employee';
import type {
  RosterRuleKind,
  RosterSaveResult,
  RosterScheduleRule,
  ShiftEditorDraft,
  WeekDayShiftEntry,
  WeekDayState,
  WeekScheduleTab,
} from '../types/Rostering';
import { excludeDayFromList, formatTimeValue, normalizeDaysOfWeek, ruleAppliesToWeekDay } from '../utils/rosterUtils';
import { normalizeStaffScheduleStatus, scheduleStatusToRuleType } from '../utils/scheduleStatusUtils';
import { getTabEntries, isTabDayActive } from '../utils/weekScheduleUtils';

const ROSTERING_API_BASE = '/api/v1/rostering';

const pickItems = <T,>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as { items?: unknown[] }).items)) {
    return (payload as { items: T[] }).items;
  }

  return [];
};

const toQueryString = (params: Record<string, string | boolean | undefined>): string => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  });
  const query = search.toString();
  return query ? `?${query}` : '';
};

const mapRosterEmployee = (value: any): Employee => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  email: String(value?.email ?? ''),
  positionName: value?.positionName ? String(value.positionName) : undefined,
  departmentId: value?.departmentId ? String(value.departmentId) : undefined,
  departmentName: value?.departmentName ? String(value.departmentName) : undefined,
  locationId: value?.locationId ? String(value.locationId) : undefined,
  locationName: value?.locationName ? String(value.locationName) : undefined,
  isActive: value?.isActive !== false,
  requirePasswordChangeOnFirstLogin: false,
});

const listRosterEmployeesApi = async (): Promise<Employee[]> => {
  const payload = await apiRequest<unknown>(`${ROSTERING_API_BASE}/employees`);
  return pickItems<any>(payload).map(mapRosterEmployee);
};

const listRosterScheduleRulesApi = async (type: 'staffShift' | 'staffTimeOff') => {
  const query = toQueryString({ type });
  const payload = await apiRequest<unknown>(`${ROSTERING_API_BASE}/schedule-rules${query}`);
  return pickItems<Record<string, unknown>>(payload);
};

const createRosterScheduleRuleApi = async (input: CreateScheduleRuleInput) => {
  const payload = await apiRequest<unknown>(`${ROSTERING_API_BASE}/schedule-rules`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return payload as Record<string, unknown>;
};

const updateRosterScheduleRuleApi = async (ruleId: string, input: UpdateScheduleRuleInput) => {
  const payload = await apiRequest<unknown>(`${ROSTERING_API_BASE}/schedule-rules/${ruleId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return payload as Record<string, unknown>;
};

const deleteRosterScheduleRuleApi = async (ruleId: string): Promise<void> => {
  await apiRequest<unknown>(`${ROSTERING_API_BASE}/schedule-rules/${ruleId}`, {
    method: 'DELETE',
  });
};

const normalizeTimeForApi = (value?: string): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return `${trimmed}:00`;
  }

  if (/^\d{2}:\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  return undefined;
};

const normalizeRepeatPattern = (value: unknown): ScheduleRepeatPattern => {
  const normalized = String(value ?? 'weekly').trim().toLowerCase();
  if (normalized === 'none' || normalized === 'monthly' || normalized === 'yearly' || normalized === 'weekly') {
    return normalized;
  }

  return 'weekly';
};

const normalizeRosterRuleType = (value: unknown): RosterRuleKind | null => {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'staffshift') {
    return 'staffShift';
  }

  if (normalized === 'stafftimeoff') {
    return 'staffTimeOff';
  }

  return null;
};

const normalizeRosterScheduleRule = (value: Record<string, unknown>): RosterScheduleRule | null => {
  const raw = value as Record<string, unknown>;
  const type = normalizeRosterRuleType(raw.type);
  if (!type) {
    return null;
  }

  return {
    id: String(raw.id ?? ''),
    type,
    scheduleStatus: normalizeStaffScheduleStatus(raw.scheduleStatus, type),
    staffId: raw.staffId ? String(raw.staffId) : undefined,
    repeatPattern: normalizeRepeatPattern(raw.repeatPattern),
    isAllDay: Boolean(raw.isAllDay),
    daysOfWeek: normalizeDaysOfWeek(raw.daysOfWeek),
    startDate: raw.startDate ? String(raw.startDate) : undefined,
    endDate: raw.endDate ? String(raw.endDate) : undefined,
    startTime: raw.startTime ? String(raw.startTime) : undefined,
    endTime: raw.endTime ? String(raw.endTime) : undefined,
    startAtUtc: raw.startAtUtc ? String(raw.startAtUtc) : undefined,
    endAtUtc: raw.endAtUtc ? String(raw.endAtUtc) : undefined,
    isEnabled: raw.isEnabled !== false,
  };
};

const DAY_INDEX_TO_NAME: DayOfWeek[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const toDayNames = (days: number[]): DayOfWeek[] => (
  days.map((dayIndex) => DAY_INDEX_TO_NAME[dayIndex] ?? 'monday')
);

const mergeSaveResults = (left: RosterSaveResult, right: RosterSaveResult): RosterSaveResult => {
  const rules = [...left.rules];

  for (const rule of right.rules) {
    const index = rules.findIndex((item) => item.id === rule.id);
    if (index >= 0) {
      rules[index] = rule;
    } else {
      rules.push(rule);
    }
  }

  return {
    rules,
    removedRuleIds: Array.from(new Set([...left.removedRuleIds, ...right.removedRuleIds])),
  };
};

const timesMatch = (left?: string, right?: string): boolean => (
  formatTimeValue(left) === formatTimeValue(right)
);

const buildEntryDraft = (
  employeeId: string,
  dayIndex: number,
  entry: WeekDayShiftEntry,
  existing?: RosterScheduleRule,
): ShiftEditorDraft => {
  const isMultiDay = Boolean(entry.sourceDaysOfWeek && entry.sourceDaysOfWeek.length > 1 && entry.ruleId);
  const changedFromSource = existing && (
    !timesMatch(entry.startTime, existing.startTime)
    || !timesMatch(entry.endTime, existing.endTime)
    || entry.scheduleStatus !== existing.scheduleStatus
  );

  const type = scheduleStatusToRuleType(entry.scheduleStatus);

  if (isMultiDay && changedFromSource) {
    return {
      type,
      scheduleStatus: entry.scheduleStatus,
      staffId: employeeId,
      daysOfWeek: [dayIndex],
      startTime: entry.startTime,
      endTime: entry.endTime,
      startDate: existing?.startDate || '',
      endDate: existing?.endDate || '',
      isEnabled: true,
      editScope: 'singleDay',
      focusDayIndex: dayIndex,
      splitFromRuleId: entry.ruleId,
      sourceDaysOfWeek: entry.sourceDaysOfWeek,
    };
  }

  return {
    id: entry.ruleId,
    type,
    scheduleStatus: entry.scheduleStatus,
    staffId: employeeId,
    daysOfWeek: [dayIndex],
    startTime: entry.startTime,
    endTime: entry.endTime,
    startDate: existing?.startDate || '',
    endDate: existing?.endDate || '',
    isEnabled: true,
    editScope: 'allDays',
  };
};

const applyResultToWorkingRules = (
  rules: RosterScheduleRule[],
  result: RosterSaveResult,
): RosterScheduleRule[] => {
  const withoutRemoved = rules.filter((rule) => !result.removedRuleIds.includes(rule.id));
  const withoutUpdated = withoutRemoved.filter(
    (rule) => !result.rules.some((saved) => saved.id === rule.id),
  );

  return [...withoutUpdated, ...result.rules];
};

const entryMatchesRule = (
  entry: WeekDayShiftEntry,
  dayIndex: number,
  rule: RosterScheduleRule,
): boolean => (
  entry.scheduleStatus === rule.scheduleStatus
  && timesMatch(entry.startTime, rule.startTime)
  && timesMatch(entry.endTime, rule.endTime)
  && rule.daysOfWeek.length === 1
  && rule.daysOfWeek[0] === dayIndex
);

const toCreatePayload = (draft: ShiftEditorDraft): CreateScheduleRuleInput => {
  const type = scheduleStatusToRuleType(draft.scheduleStatus);

  return {
    type,
    scheduleStatus: draft.scheduleStatus,
    staffId: draft.staffId,
    repeatPattern: 'weekly',
    daysOfWeek: toDayNames(draft.daysOfWeek),
    startDate: draft.startDate || undefined,
    endDate: draft.endDate || undefined,
    startTime: normalizeTimeForApi(draft.startTime),
    endTime: normalizeTimeForApi(draft.endTime),
    isEnabled: draft.isEnabled,
  };
};

const toUpdatePayload = (draft: ShiftEditorDraft): UpdateScheduleRuleInput => ({
  type: scheduleStatusToRuleType(draft.scheduleStatus),
  scheduleStatus: draft.scheduleStatus,
  staffId: draft.staffId,
  daysOfWeek: toDayNames(draft.daysOfWeek),
  startDate: draft.startDate || undefined,
  endDate: draft.endDate || undefined,
  startTime: normalizeTimeForApi(draft.startTime),
  endTime: normalizeTimeForApi(draft.endTime),
  isEnabled: draft.isEnabled,
});

export const rosteringService = {
  listEmployees: async (): Promise<Employee[]> => {
    const employees = await listRosterEmployeesApi();
    return employees
      .filter((employee) => employee.isActive)
      .sort((left, right) => left.name.localeCompare(right.name));
  },

  listRosterRules: async (): Promise<RosterScheduleRule[]> => {
    const [shifts, timeOff] = await Promise.all([
      listRosterScheduleRulesApi('staffShift'),
      listRosterScheduleRulesApi('staffTimeOff'),
    ]);

    return [...shifts, ...timeOff]
      .map((rule) => normalizeRosterScheduleRule(rule as unknown as Record<string, unknown>))
      .filter((rule): rule is RosterScheduleRule => Boolean(rule));
  },

  saveShift: async (draft: ShiftEditorDraft): Promise<RosterSaveResult> => {
    if (draft.editScope === 'singleDay' && draft.splitFromRuleId && typeof draft.focusDayIndex === 'number') {
      const sourceDays = draft.sourceDaysOfWeek ?? [];
      const remainingDays = excludeDayFromList(sourceDays, draft.focusDayIndex);
      const rules: RosterScheduleRule[] = [];
      const removedRuleIds: string[] = [];

      if (remainingDays.length === 0) {
        await deleteRosterScheduleRuleApi(draft.splitFromRuleId);
        removedRuleIds.push(draft.splitFromRuleId);
      } else {
        const updatedSource = await updateRosterScheduleRuleApi(draft.splitFromRuleId, {
          daysOfWeek: toDayNames(remainingDays),
        });
        const normalizedSource = updatedSource
          ? normalizeRosterScheduleRule(updatedSource as unknown as Record<string, unknown>)
          : null;
        if (normalizedSource) {
          rules.push(normalizedSource);
        }
      }

      const created = await createRosterScheduleRuleApi(toCreatePayload(draft));
      const normalizedCreated = created
        ? normalizeRosterScheduleRule(created as unknown as Record<string, unknown>)
        : null;
      if (normalizedCreated) {
        rules.push(normalizedCreated);
      }

      return { rules, removedRuleIds };
    }

    const payload = draft.id
      ? await updateRosterScheduleRuleApi(draft.id, toUpdatePayload(draft))
      : await createRosterScheduleRuleApi(toCreatePayload(draft));

    const normalized = payload
      ? normalizeRosterScheduleRule(payload as unknown as Record<string, unknown>)
      : null;

    return {
      rules: normalized ? [normalized] : [],
      removedRuleIds: [],
    };
  },

  removeDayFromShift: async (
    ruleId: string,
    dayIndex: number,
    sourceDays: number[],
  ): Promise<RosterSaveResult> => {
    const remainingDays = excludeDayFromList(sourceDays, dayIndex);
    const removedRuleIds: string[] = [];

    if (remainingDays.length === 0) {
      await deleteRosterScheduleRuleApi(ruleId);
      return { rules: [], removedRuleIds: [ruleId] };
    }

    const updated = await updateRosterScheduleRuleApi(ruleId, {
      daysOfWeek: toDayNames(remainingDays),
    });
    const normalized = updated
      ? normalizeRosterScheduleRule(updated as unknown as Record<string, unknown>)
      : null;

    return {
      rules: normalized ? [normalized] : [],
      removedRuleIds,
    };
  },

  deleteShift: async (ruleId: string): Promise<void> => {
    await deleteRosterScheduleRuleApi(ruleId);
  },

  saveEmployeeWeekTab: async (
    employeeId: string,
    dayStates: WeekDayState[],
    tab: WeekScheduleTab,
    allRules: RosterScheduleRule[],
  ): Promise<RosterSaveResult> => {
    let workingRules = allRules.filter((rule) => rule.staffId === employeeId);
    if (tab === 'onCall') {
      workingRules = workingRules.filter((rule) => rule.type === 'staffTimeOff');
    }

    let aggregate: RosterSaveResult = { rules: [], removedRuleIds: [] };

    for (const dayState of dayStates) {
      const desiredEntries = isTabDayActive(dayState, tab) ? getTabEntries(dayState, tab) : [];
      const existingForDay = workingRules.filter(
        (rule) => rule.daysOfWeek.includes(dayState.dayIndex) && ruleAppliesToWeekDay(rule, dayState),
      );

      for (const existing of existingForDay) {
        const stillDesired = desiredEntries.some((entry) => entry.ruleId === existing.id);
        if (stillDesired) {
          continue;
        }

        const result = existing.daysOfWeek.length === 1
          ? await (async () => {
            await deleteRosterScheduleRuleApi(existing.id);
            return { rules: [], removedRuleIds: [existing.id] } satisfies RosterSaveResult;
          })()
          : await rosteringService.removeDayFromShift(existing.id, dayState.dayIndex, existing.daysOfWeek);

        aggregate = mergeSaveResults(aggregate, result);
        workingRules = applyResultToWorkingRules(workingRules, result);
      }

      for (const entry of desiredEntries) {
        const existing = entry.ruleId
          ? workingRules.find((rule) => rule.id === entry.ruleId)
          : undefined;

        if (existing && entryMatchesRule(entry, dayState.dayIndex, existing)) {
          continue;
        }

        const draft = buildEntryDraft(employeeId, dayState.dayIndex, entry, existing);
        const result = await rosteringService.saveShift(draft);
        aggregate = mergeSaveResults(aggregate, result);
        workingRules = applyResultToWorkingRules(workingRules, result);
      }
    }

    return aggregate;
  },
};

export const listRosterEmployees = rosteringService.listEmployees;
export const listRosterRules = rosteringService.listRosterRules;
export const saveRosterShift = rosteringService.saveShift;
export const saveEmployeeWeekTab = rosteringService.saveEmployeeWeekTab;
export const removeRosterDayFromShift = rosteringService.removeDayFromShift;
export const deleteRosterShift = rosteringService.deleteShift;
